/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/ScopeAgGridContextMenu.tsx
 * FILE_ID: QB-AGGRID-CTXMENU-001
 * PURPOSE: SAM-style right-click context menu on AG-Grid rows.
 *          Renders an absolutely-positioned popup with Insert above /
 *          below / Duplicate / Delete; auto-closes on outside-click
 *          and on Escape.
 * WHY: AG-Grid Community doesn't ship a context menu (the
 *      getContextMenuItems hook is Enterprise-only). SAM's grid has
 *      one and estimators expect it. This component is the
 *      Community-tier replacement, mounted by ScopeAgGridSection.
 * FLOW:
 *   ┌───────────────────┐   ┌────────────────────┐   ┌────────────────────┐
 *   │ AG-Grid           │ → │ section state      │ → │ ScopeAgGridContext │
 *   │ onCellContextMenu │   │ {open, x, y, rowId}│   │ Menu renders popup │
 *   └───────────────────┘   └────────────────────┘   └────────────────────┘
 * DEPENDENCIES: lucide-react icons; pure pointer-event handling.
 * RELATED:
 *   - QB-AGGRID-LAZY-001 (lazy-aggrid-impl.tsx) — captures the event
 *   - QB-AGGRID-SECTION-001 (ScopeAgGridSection.tsx) — host
 */

import { useRef } from 'react'
import { useCloseOnOutsidePointer } from '@quotech/ui'
import { ArrowUp, ArrowDown, Copy, Trash2 } from 'lucide-react'

export interface ScopeAgGridContextMenuProps {
  open: boolean
  /** Pixel coords where the user right-clicked (clientX / clientY). */
  x: number
  y: number
  onClose: () => void
  onInsertAbove: () => void
  onInsertBelow: () => void
  onDuplicate: () => void
  onDelete: () => void
}

interface MenuItem {
  id: string
  icon: typeof ArrowUp
  label: string
  onClick: () => void
  danger?: boolean
}

export default function ScopeAgGridContextMenu({
  open,
  x,
  y,
  onClose,
  onInsertAbove,
  onInsertBelow,
  onDuplicate,
  onDelete,
}: ScopeAgGridContextMenuProps) {
  const ref = useRef<HTMLDivElement | null>(null)

  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(open, onClose, [ref])

  if (!open) return null

  const items: MenuItem[] = [
    {
      id: 'insert-above',
      icon: ArrowUp,
      label: 'Insert row above',
      onClick: () => {
        onInsertAbove()
        onClose()
      },
    },
    {
      id: 'insert-below',
      icon: ArrowDown,
      label: 'Insert row below',
      onClick: () => {
        onInsertBelow()
        onClose()
      },
    },
    {
      id: 'duplicate',
      icon: Copy,
      label: 'Duplicate row',
      onClick: () => {
        onDuplicate()
        onClose()
      },
    },
    {
      id: 'delete',
      icon: Trash2,
      label: 'Delete row',
      danger: true,
      onClick: () => {
        onDelete()
        onClose()
      },
    },
  ]

  // Clamp the popup so it never falls off the right / bottom edge of
  // the viewport. 240×220 are conservative bounds for the menu.
  const adjustedX = Math.min(x, window.innerWidth - 240)
  const adjustedY = Math.min(y, window.innerHeight - 220)

  return (
    <div
      ref={ref}
      role="menu"
      aria-orientation="vertical"
      className="fixed z-50 min-w-[200px] rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg py-1 text-sm"
      style={{ left: adjustedX, top: adjustedY }}
    >
      {items.map((item, idx) => {
        const Icon = item.icon
        const isSeparatorAbove = idx === items.length - 1
        return (
          <div key={item.id}>
            {isSeparatorAbove && (
              <div className="my-1 h-px bg-slate-200 dark:bg-slate-700" />
            )}
            <button
              type="button"
              role="menuitem"
              onClick={item.onClick}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${
                item.danger
                  ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40'
                  : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span>{item.label}</span>
            </button>
          </div>
        )
      })}
    </div>
  )
}
